/**
 * Created by olga on 21.10.2024
 */

const FocusManager = function () {
    cleverapps.EventEmitter.call(this);

    this.stack = [];

    this.pushState({
        focus: false,
        controls: [],
        eventNodes: []
    });
    this.pushState({
        focus: "initialize",
        controls: [],
        eventNodes: []
    });

    this.focusHistory = [];
    this.tempControls = {};
    this.lastFocus = undefined;

    this.lastControlsVisible = {};
    this.onControlsListeners = {};
    this.controlsOptions = {};

    this.pendingListeners = [];
};

FocusManager.prototype = Object.create(cleverapps.EventEmitter.prototype);
FocusManager.prototype.constructor = FocusManager;

FocusManager.prototype.showControlsTemporary = function (controls) {
    cleverapps.toArray(controls).forEach((control) => {
        this.tempControls[control] = true;
        this.updateControlVisibility(control);
    });
};

FocusManager.prototype.hideControlsTemporary = function (controls) {
    cleverapps.toArray(controls).forEach((control) => {
        delete this.tempControls[control];
        this.updateControlVisibility(control);
    });
};

FocusManager.prototype.clearTempControls = function () {
    this.tempControls = {};
};

FocusManager.prototype.registerControl = function (name, listener, hiddenByDefault) {
    if (typeof listener === "object") {
        listener = listener.createListener(listener.toggle.bind(listener));
    }

    this.onControlsListeners[name] = listener;
    this.lastControlsVisible[name] = undefined;

    this.setHiddenByDefault(name, hiddenByDefault);
};

FocusManager.prototype.setHiddenByDefault = function (name, hiddenByDefault) {
    this.controlsOptions[name] = {
        hiddenByDefault
    };

    const index = this.controls.indexOf(name);

    if (this.shouldBeVisible(name) && index === -1) {
        this.controls.push(name);
    }

    if (this.isHiddenByDefault(name) && index !== -1) {
        this.controls.splice(index, 1);
    }

    this.updateControlVisibility(name, cleverapps.silentIntro);
};

FocusManager.prototype.removeControl = function (name) {
    delete this.onControlsListeners[name];
    delete this.lastControlsVisible[name];
};

FocusManager.prototype.updateControlsVisibility = function (silent) {
    for (const key in this.onControlsListeners) {
        this.updateControlVisibility(key, silent);
    }
};

FocusManager.prototype.updateControlVisibility = function (key, silent) {
    const visible = this.controls.indexOf(key) !== -1 || this.tempControls[key] !== undefined;

    if (this.lastControlsVisible[key] !== visible && this.onControlsListeners[key]) {
        this.onControlsListeners[key](visible, silent);
        this.lastControlsVisible[key] = visible;
    }
};

FocusManager.prototype.setControlsVisible = function (silent) {
    this.clearUpdateTimeout();

    if (!this.isFocused() && !cleverapps.gameModes.noControls) {
        this.setControls(Object.keys(this.onControlsListeners).filter((control) => this.shouldBeVisible(control)));
    }

    if (silent) {
        this.updateControlsVisibility(true);
    } else {
        this.updateTimeout = setTimeout(this.updateControlsVisibility.bind(this), 100);
    }
};

FocusManager.prototype.isHiddenByDefault = function (name) {
    const hiddenByDefault = this.controlsOptions[name] && this.controlsOptions[name].hiddenByDefault;
    return hiddenByDefault === true || hiddenByDefault && cleverapps.environment.hasScene(hiddenByDefault);
};

// A control registered by the scene it belongs to says plainly that it starts hidden - one registered
// once for every scene names the scenes it starts hidden on.
FocusManager.prototype.shouldBeVisible = function (control) {
    return !this.isFocused() && !this.isHiddenByDefault(control);
};

FocusManager.prototype.clearUpdateTimeout = function () {
    if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = undefined;
    }
};

FocusManager.prototype.debugMessage = function (message) {
    if (cleverapps.config.debugMode && !connector.info.isNative) {
        throw message;
    } else {
        cleverapps.throwAsync(message);
    }
};

FocusManager.prototype.displayWhenFreeFocus = function (options) {
    if (!this.isFocused()) {
        this.display(options);
    } else {
        this.whenFreeFocus(this.display.bind(this, options));
    }
};

FocusManager.prototype.whenFreeFocus = function (listener) {
    if (!this.isFocused()) {
        listener();
    } else {
        this.pendingListeners.push(listener);
    }
};

FocusManager.prototype.distract = function (options) {
    if (!this.isFocused()) {
        this.debugMessage(`Distract without focus! ${options.focus}`);
    }

    if (this.focus === options.focus) {
        this.debugMessage(`Distract same focus!${options.focus}`);
        return;
    }

    if (options.filter && !options.filter() || this.getState().important) {
        return;
    }

    while (this.stack.length > 1) {
        this.popState();
    }

    this.grabFocus(options.focus, options);
    this.compound(this.freeFocus.bind(this, options.focus), options.actions || [options.action]);
};

FocusManager.prototype.display = function (options) {
    if (this.isFocused() && !options.stack || this.getState().important) {
        return;
    }

    if (options.filter && !options.filter()) {
        return;
    }

    this.grabFocus(options.focus, options);
    this.compound(this.freeFocus.bind(this, options.focus), options.actions || [options.action]);
};

FocusManager.prototype.compound = function (f, actions, index, state) {
    const current = this.stack[this.stack.length - 1];

    index = index || 0;
    state = state || current;

    if (current !== state) {
        if (this.stack.includes(state)) {
            if (state.action) {
                cleverapps.throwAsync(`state already had assigned action ${state.focus} ${current.focus}`);
            }
            state.action = this.compound.bind(this, f, actions, index, state);
        }
        return;
    }

    if (actions.length === index) {
        f();
        return;
    }

    this.setEventNodes([]);

    actions[index](cleverapps.once(this.compound.bind(this, f, actions, index + 1, state)));
};

FocusManager.prototype.showControlsWhileFocused = function (controls, silent) {
    cleverapps.toArray(controls).forEach(function (control) {
        if (this.controls.indexOf(control) === -1) {
            this.controls.push(control);
            this.updateControlVisibility(control, silent);
        } else if (silent && this.onControlsListeners[control]) {
            this.onControlsListeners[control](true, silent);
        }
    }, this);
};

FocusManager.prototype.isControlEnabled = function (control) {
    return cleverapps.intersect(this.controls, control).length > 0;
};

FocusManager.prototype.hideAllControls = function (silent) {
    this.setControls([]);
    this.updateControlsVisibility(silent);
};

FocusManager.prototype.hideControlsWhileFocused = function (controls) {
    cleverapps.toArray(controls).forEach(function (control) {
        const index = this.controls.indexOf(control);
        if (index !== -1 && !this.shouldBeVisible(control)) {
            this.controls.splice(index, 1);
            this.updateControlVisibility(control);
        }
    }, this);
};

FocusManager.prototype.checkEventNode = function (node) {
    if (node.alwaysOn) {
        return true;
    }

    const scene = cleverapps.Scenes.GetRunningScene();

    if (this.releaseFocusTimeout !== undefined) {
        return false;
    }

    if (!this.isFocused()) {
        return true;
    }

    let eventNodes = this.eventNodes;

    if (scene.toolMenu) {
        eventNodes = eventNodes.concat(scene.toolMenu, scene.toolMenu.opener);
    }

    if (!eventNodes.length) {
        return false;
    }

    if (eventNodes[0] === scene) {
        return true;
    }

    while (node) {
        if (eventNodes.indexOf(node) !== -1) {
            return true;
        }
        node = node.parent;
    }
    return false;
};

FocusManager.prototype.setEventNodes = function (eventNodes) {
    const state = this.stack[this.stack.length - 1];
    state.eventNodes = this.eventNodes = eventNodes;
};

FocusManager.prototype.setControls = function (controls) {
    const state = this.stack[this.stack.length - 1];
    state.controls = this.controls = controls;
};

FocusManager.prototype.grabFocus = function (name, options) {
    this.clearReleaseFocusTimeout();

    options = options || {};

    console.log(`Grab focus: ${name}`);

    let controls = cleverapps.toArray(options.control || []);
    if (options.keepControls) {
        controls = this.controls;
    }

    this.saveHistory(`+${name}`);
    this.pushState({
        focus: name,
        important: options.important,
        controls: controls.slice(),
        eventNodes: []
    });

    this.setControlsVisible(cleverapps.silentIntro);

    this.trigger("changeFocus");
};

FocusManager.prototype.freeFocus = function (name) {
    if (this.focus !== name) {
        console.log(`Wrong focus clearing: ${this.focus} > ${name}`);
        return;
    }

    console.log(`Focus freed: ${this.focus}`);

    this.lastFocus = this.focus;
    this.saveHistory(`-${this.focus}`);
    this.popState();

    this.setControlsVisible(cleverapps.silentIntro);

    this.trigger("changeFocus");

    this.resumeCompound();

    if (!this.isFocused()) {
        cleverapps.bundleLoader.clearUnusedResources();
        this.clearReleaseFocusTimeout();
        this.releaseFocusTimeout = setTimeout(() => this.triggerFocusLostListeners(), 0);
    }
};

FocusManager.prototype.clearReleaseFocusTimeout = function () {
    if (this.releaseFocusTimeout !== undefined) {
        clearTimeout(this.releaseFocusTimeout);
        delete this.releaseFocusTimeout;
    }
};

FocusManager.prototype.triggerFocusLostListeners = function () {
    this.clearReleaseFocusTimeout();

    while (this.pendingListeners.length > 0 && !this.isFocused()) {
        const listener = this.pendingListeners.shift();
        listener();
    }

    if (!this.isFocused()) {
        cleverapps.placements.onFocusLostListenerPlacements();
    }
};

FocusManager.prototype.saveHistory = function (focus) {
    this.focusHistory.push(focus);
    this.focusHistory = this.focusHistory.slice(-20);
};

FocusManager.prototype.pushState = function (state) {
    this.stack.push(state);

    this.focus = state.focus;
    this.setControls(state.controls);
    this.setEventNodes(state.eventNodes);
};

FocusManager.prototype.popState = function () {
    if (this.stack.length === 1) {
        this.debugMessage("Attempt to free default state");
        return;
    }

    let state = this.stack.pop();
    state.controls = [];
    state.eventNodes = [];
    state.action = undefined;

    if (state.window && !state.window.closed) {
        this.closeWindow(state.window);
    }

    state = this.stack[this.stack.length - 1];
    this.focus = state.focus;
    this.setControls(state.controls);
    this.setEventNodes(state.eventNodes);

    setTimeout(() => this.notifyPopWindow(state), 0);
};

FocusManager.prototype.getState = function () {
    return this.stack[this.stack.length - 1];
};

FocusManager.prototype.resumeCompound = function () {
    const state = this.stack[this.stack.length - 1];

    const action = state.action;
    state.action = undefined;

    if (action) {
        action();
    }
};

FocusManager.prototype.isFocused = function () {
    return this.focus !== false;
};

FocusManager.prototype.getFocus = function () {
    return this.focus;
};

FocusManager.prototype.findFocus = function (...args) {
    const focuses = new Set([...args]);

    return this.stack.find((state) => focuses.has(state.focus));
};

FocusManager.prototype.addWindow = function (window) {
    const state = this.getState();

    if (state.window) {
        this.debugMessage(`Focus ${state.focus} already has window!`);
    }

    window.focusState = state;
    window.focus = state.focus;

    state.window = window;
};

FocusManager.prototype.deleteWindow = function (window) {
    const state = window.focusState;

    if (state.window === window) {
        state.window = undefined;
    }
};

FocusManager.prototype.currentWindow = function () {
    for (let i = this.stack.length - 1; i >= 0; i--) {
        const state = this.stack[i];
        if (state.window) {
            return state.window;
        }
    }
};

FocusManager.prototype.findWindow = function (...args) {
    return this.listWindows().find((window) => cleverapps.oneOf(window, ...args));
};

FocusManager.prototype.listWindows = function () {
    return this.stack.map((state) => state.window).filter(Boolean);
};

FocusManager.prototype.resetWindows = function () {
    this.listWindows().forEach((window) => this.deleteWindow(window));
};

FocusManager.prototype.closeWindow = function (window) {
    if (window.closed) {
        return;
    }

    if (typeof window.close === "function") {
        window.close();
    } else {
        ComponentMap.get(window, WindowComponent).closeWindow();
    }
};

FocusManager.prototype.closeAllWindows = function () {
    while (this.currentWindow()) {
        const window = this.currentWindow();
        this.closeWindow(window);
        this.deleteWindow(window);
    }
    WindowComponent.hideLoading();
};

FocusManager.prototype.notifyPopWindow = function (state) {
    if (!state.window && state.onceNoWindowsListener) {
        const onceNoWindowsListener = state.onceNoWindowsListener;
        state.onceNoWindowsListener = undefined;
        onceNoWindowsListener();
    }

    if (!this.currentWindow() && this.onceTutorialNoWindowsListener) {
        const onceTutorialNoWindowsListener = this.onceTutorialNoWindowsListener;
        this.onceTutorialNoWindowsListener = undefined;
        onceTutorialNoWindowsListener();
    }
};

Object.defineProperty(FocusManager.prototype, "onceNoWindowsListener", {
    get: function () {
        const state = this.getState();
        return state.onceNoWindowsListener;
    },

    set: function (value) {
        const state = this.getState();
        state.onceNoWindowsListener = value;
    }
});